import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCurrentSection, setFormData, toggleSectionCollapse, updateReasonData } from '@/store/slices/formSlice'
import { FormSection as FormSectionInterface } from '@/types/FormSectionInterface'
import {
  BiologicalParentData,
  CollectionType,
  DEFAULT_FORM_DATA,
  getCollectionTypeLabel,
  OrderFormInfoData,
  RequisitionFormData,
  Sex,
  TestType,
} from '@/types/RequisitionFormTypes'
import { createOrder, getOrderByPatient, OrderStatus, updateOrder } from '@/utils/DatabaseOperations'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import React, { ReactNode, useRef } from 'react'
import BiologicalParentInformation from '../forms/BiologicalParentInformation'
import ClinicalInformation from '../forms/ClinicalInformation'
import Consent from '../forms/Consent'
import FamilyHistory from '../forms/FamilyHistory'
import OrderType from '../forms/OrderType'
import PatientInformation from '../forms/PatientInformation'
import ReasonForTesting from '../forms/ReasonForTesting'
import Review from '../forms/Review'
import '../styles/portal/forms/RequisitionForm.css'
import '../styles/portal/layout/layout.css'

// Default implementation of FormSection interface
const defaultFormSection: FormSectionInterface = {
  isSectionComplete: () => false,
  renderSection: () => <div></div>,
  renderSectionInfo: () => <div></div>,
}

interface RequisitionFormProps {
  children?: ReactNode
  data?: OrderFormInfoData | null
  onSubmit?: (formData: RequisitionFormData) => void
}

type NavigationSection =
  | typeof SECTION_NAMES.ORDER_TYPE
  | typeof SECTION_NAMES.PATIENT_INFORMATION
  | typeof SECTION_NAMES.CLINICAL_INFORMATION
  | typeof SECTION_NAMES.REASON_FOR_TESTING
  | typeof SECTION_NAMES.FAMILY_HISTORY
  | typeof SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1
  | typeof SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2
  | typeof SECTION_NAMES.CONSENT
  | typeof SECTION_NAMES.REVIEW_SUBMIT

// Constants for section names
const SECTION_NAMES = {
  ORDER_TYPE: 'order-type',
  PATIENT_INFORMATION: 'patient-information',
  CLINICAL_INFORMATION: 'clinical-information',
  REASON_FOR_TESTING: 'reason-for-testing',
  FAMILY_HISTORY: 'family-history',
  BIOLOGICAL_PARENT_INFORMATION_1: 'biological-parent-information-1',
  BIOLOGICAL_PARENT_INFORMATION_2: 'biological-parent-information-2',
  CONSENT: 'consent',
  REVIEW_SUBMIT: 'review-submit',
} as const

// Enum for form sections
enum FormSection {
  ORDER_TYPE = 1,
  PATIENT_INFORMATION = 2,
  CLINICAL_INFORMATION = 3,
  REASON_FOR_TESTING = 4,
  FAMILY_HISTORY = 5,
  BIOLOGICAL_PARENT_INFORMATION_1 = 6,
  BIOLOGICAL_PARENT_INFORMATION_2 = 7,
  CONSENT = 8,
  REVIEW_SUBMIT = 9,
}

const RequisitionForm: React.FC<RequisitionFormProps> = ({ children, data, onSubmit }) => {
  // Define completion colors once
  const completeColor = 'bg-[#D3E8BE]'
  const incompleteColor = 'bg-[#DA7B7B]'

  // Local state for loading
  const [isLoading, setIsLoading] = React.useState(false)

  // Redux state and dispatch
  const dispatch = useAppDispatch()
  const { formData, currentSection, collapsedSections, isFormInitialized } = useAppSelector((state) => state.form)

  // Initialize form data only once when data is first provided
  React.useEffect(() => {
    if (data && !isFormInitialized) {
      // Create initial form data from the provided data
      const initialFormData: RequisitionFormData = {
        ...DEFAULT_FORM_DATA,
        patient: {
          firstName: data.patient.name.firstName || '',
          lastName: data.patient.name.lastName || '',
          middleName: data.patient.name.middleName || '',
          dateOfBirth: data.patient.birthDate ? data.patient.birthDate.toISOString().split('T')[0] : '',
          sex: data.patient.sex || Sex.Unknown,
          mrn: data.patient.mrn || '',
          collectionType: CollectionType.WholeBloodEDTA,
        },
        consentFormData: {
          medicalProfessionalName: data.medicalProfessionalInfo?.name || '',
          npi: data.medicalProfessionalInfo?.npi || '',
          signatureDate: new Date().toISOString().split('T')[0],
          signature: '',
        },
      }

      // Set the initial form data in Redux
      dispatch(setFormData(initialFormData))
    }
  }, [data, isFormInitialized, dispatch])

  const handleToggleSectionCollapse = (sectionId: string) => {
    dispatch(toggleSectionCollapse(sectionId))
  }

  const handleSetCurrentSection = (section: NavigationSection) => {
    dispatch(setCurrentSection(section))
  }

  // Reset scroll position when section changes
  React.useEffect(() => {
    const scrollContainer = document.querySelector('.flex-1.overflow-y-auto')
    if (scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [currentSection])

  // Check if parent sections should be shown based on selected test type
  // Parent sections are hidden by default and only shown for test types that require them
  const shouldShowParentSections = () => {
    // Only show parent sections for test types that require them (not F10-01)
    return (
      formData.order.selectedOption !== TestType.rWGS_Patient_Only && formData.order.selectedOption !== TestType.NONE
    )
  }

  // Check if we should show only one parent section (for F10-02)
  const shouldShowSingleParentSection = () => {
    return formData.order.selectedOption === TestType.rWGS_Duo
  }

  // Temporarily bypass consent section
  const shouldShowConsentSection = () => false

  // Refs for form sections to check completion status
  const orderTypeRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const patientInfoRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const clinicalInfoRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const reasonForTestingRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const familyHistoryRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const motherInfoRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const fatherInfoRef = useRef<FormSectionInterface | null>(defaultFormSection)
  const consentRef = useRef<FormSectionInterface | null>(defaultFormSection)

  // Map section names to their corresponding refs
  const sectionRefs: Record<string, React.RefObject<FormSectionInterface | null>> = {
    order: orderTypeRef,
    patient: patientInfoRef,
    clinical: clinicalInfoRef,
    reason: reasonForTestingRef,
    family: familyHistoryRef,
    mother: motherInfoRef,
    father: fatherInfoRef,
    consent: consentRef,
  }

  // Function to render completion indicator
  const renderCompletionIndicator = (section: keyof RequisitionFormData | 'review') => {
    if ((section === 'review' && isFormComplete()) || (section !== 'review' && isSectionComplete(section))) {
      return (
        <div className={`flex h-5 w-5 items-center justify-center rounded ${completeColor}`}>
          <img src="/icons/check.svg" alt="Complete" className="h-3 w-3" />
        </div>
      )
    } else {
      return <div className={`h-2 w-2 rounded-full ${incompleteColor}`}></div>
    }
  }

  // Mark section as visited when user navigates to it
  const markSectionAsVisited = (section: string) => {
    if (section === 'reason') {
      dispatch(updateReasonData({ userInteracted: true }))
    }
  }

  const handleSubmit = () => {
    try {
      // Generate and download the PDF
      if (onSubmit) {
        onSubmit(formData)
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      // You might want to show an error message to the user here
    }
  }

  // Helper function to get a valid form section
  const getFormSection = (section: keyof RequisitionFormData): FormSectionInterface => {
    const ref = sectionRefs[section]
    return ref.current || defaultFormSection
  }

  // Function to check if a section is complete using the utility
  const isSectionComplete = (section: keyof RequisitionFormData): boolean => {
    return FormCompletionChecker.isSectionComplete(section, formData)
  }

  const isFormComplete = (): boolean => {
    return (
      isSectionComplete('order') &&
      isSectionComplete('patient') &&
      isSectionComplete('clinical') &&
      isSectionComplete('reason') &&
      isSectionComplete('family') &&
      (shouldShowParentSections()
        ? shouldShowSingleParentSection()
          ? isSectionComplete('mother') || isSectionComplete('father')
          : isSectionComplete('mother') && isSectionComplete('father')
        : true) &&
      (shouldShowConsentSection() ? isSectionComplete('consent') : true)
    )
  }

  // Map section names to their render functions
  const sectionRenderers: Record<string, () => React.ReactNode> = {
    [SECTION_NAMES.PATIENT_INFORMATION]: () => {
      return (
        <div className="rounded-lg bg-white p-6">
          <PatientInformation ref={patientInfoRef} data={data ?? undefined} />
        </div>
      )
    },
    [SECTION_NAMES.CLINICAL_INFORMATION]: () => {
      return (
        <div className="rounded-lg bg-white p-6">
          <ClinicalInformation ref={clinicalInfoRef} />
        </div>
      )
    },
    [SECTION_NAMES.REASON_FOR_TESTING]: () => {
      return (
        <div className="rounded-lg bg-white p-6">
          <ReasonForTesting ref={reasonForTestingRef} />
        </div>
      )
    },
    [SECTION_NAMES.FAMILY_HISTORY]: () => {
      return (
        <div className="rounded-lg bg-white p-6">
          <FamilyHistory ref={familyHistoryRef} />
        </div>
      )
    },
    [SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1]: () => {
      if (!shouldShowParentSections()) {
        return (
          <div className="rounded-lg bg-white p-6">
            Parent sections are not required for rWGS Patient Only (F10-01) test type.
          </div>
        )
      }

      // For F10-02, show a single parent section with choice between mother and father
      if (shouldShowSingleParentSection()) {
        return (
          <div className="rounded-lg bg-white p-6">
            <BiologicalParentInformation
              ref={motherInfoRef}
              key="parent"
              parentType={undefined} // This will show the selection buttons
            />
          </div>
        )
      }

      return (
        <div className="rounded-lg bg-white p-6">
          <BiologicalParentInformation ref={motherInfoRef} key="mother" parentType="mother" />
        </div>
      )
    },
    [SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2]: () => {
      if (!shouldShowParentSections()) {
        return (
          <div className="rounded-lg bg-white p-6">
            Parent sections are not required for rWGS Patient Only (F10-01) test type.
          </div>
        )
      }
      return (
        <div className="rounded-lg bg-white p-6">
          <BiologicalParentInformation ref={fatherInfoRef} key="father" parentType="father" />
        </div>
      )
    },
    [SECTION_NAMES.ORDER_TYPE]: () => {
      return (
        <div className="rounded-lg bg-white p-6">
          <OrderType ref={orderTypeRef} />
        </div>
      )
    },
    [SECTION_NAMES.CONSENT]: () => (
      <div className="rounded-lg bg-white p-6">
        <Consent ref={consentRef} data={data ?? undefined} />
      </div>
    ),
    [SECTION_NAMES.REVIEW_SUBMIT]: () => (
      <div className="rounded-lg bg-white p-6">
        <Review
          formData={formData as unknown as Record<string, unknown>}
          onConfirm={handleSubmit}
          onEdit={(section) =>
            handleSetCurrentSection(
              section as
                | 'patient-information'
                | 'clinical-information'
                | 'reason-for-testing'
                | 'family-history'
                | 'biological-parent-information-1'
                | 'biological-parent-information-2'
                | 'order-type'
                | 'consent'
                | 'review-submit'
            )
          }
          showConsentSection={shouldShowConsentSection()}
        />
      </div>
    ),
  }

  const renderSection = () => {
    const renderer = sectionRenderers[currentSection]
    return renderer ? renderer() : null
  }

  // Map section names to their info render functions
  const sectionInfoRenderers: Record<string, () => React.ReactNode> = {
    [SECTION_NAMES.ORDER_TYPE]: () => {
      // If the OrderType component is not rendered, create a temporary instance to get the info
      const orderSection = getFormSection('order')
      if (orderSection === defaultFormSection) {
        // Create a temporary OrderType component to get the info
        return (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <p className="body-text mb-3">
              Whole genome sequencing empowers clinicians to improve the time to treatment for critically ill patients,
              changing outcomes. Inflection Medicine offers two services:
            </p>
            <p className="form-label mb-2">Rapid Whole Genome Sequencing (rWGS)</p>
            <p className="body-text mb-3">
              Preliminary positive report within 5 business days, final report in 7 business days.
            </p>
            <p className="form-label mb-2">Ultra Rapid Whole Genome Sequencing (urWGS)</p>
            <p className="body-text mb-3">
              The turnaround time for ultra-rapid WGS is 3 calendar days when all necessary samples are received at the
              outset; if parental samples are delayed (up to 2 days post-patient), a final report will follow within 7
              days.
            </p>
            {formData.order.selectedOption && (
              <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-medium text-blue-800">Selected Service:</p>
                <p className="text-sm text-blue-700">{formData.order.selectedOption}</p>
              </div>
            )}
          </div>
        )
      }
      return orderSection.renderSectionInfo()
    },
    [SECTION_NAMES.PATIENT_INFORMATION]: () => {
      return getFormSection('patient').renderSectionInfo()
    },
    [SECTION_NAMES.CLINICAL_INFORMATION]: () => {
      return getFormSection('clinical').renderSectionInfo()
    },
    [SECTION_NAMES.REASON_FOR_TESTING]: () => {
      return getFormSection('reason').renderSectionInfo()
    },
    [SECTION_NAMES.FAMILY_HISTORY]: () => {
      return getFormSection('family').renderSectionInfo()
    },
    [SECTION_NAMES.REVIEW_SUBMIT]: () => {
      const CollapsibleSection = ({
        title,
        sectionId,
        children,
      }: {
        title: string
        sectionId: string
        children: React.ReactNode
      }) => {
        const isCollapsed = collapsedSections[sectionId] || false

        return (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <button
              onClick={() => handleToggleSectionCollapse(sectionId)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <span className="font-medium text-gray-800">{title}</span>
              <svg
                className={`h-5 w-5 transform transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!isCollapsed && <div className="px-4 pb-4">{children}</div>}
          </div>
        )
      }

      const ParentalConsentSection = ({
        parentData,
        parentType,
      }: {
        parentData: BiologicalParentData
        parentType: 'mother' | 'father'
      }) => {
        const parentName = `${parentData.parentFirstName || ''} ${parentData.parentLastName || ''}`.trim()
        const parentEmail = parentData.parentEmail || ''
        const displayType = parentType === 'mother' ? 'Mother' : 'Father'

        return (
          <CollapsibleSection
            title={`Parental Consent - Biological ${displayType}`}
            sectionId={`parental-consent-${parentType}`}
          >
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Biological {displayType}</div>
              {parentName ? (
                <div className="body-text space-y-1">
                  <div>
                    <span className="font-medium">Name:</span> {parentName}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span> {parentEmail || 'Not provided'}
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span> {parentData.parentPhone || 'Not provided'}
                  </div>
                  <div>
                    <span className="font-medium">Date of Birth:</span> {parentData.parentDateOfBirth || 'Not provided'}
                  </div>
                  <div>
                    <span className="font-medium">Collection Type:</span>{' '}
                    {getCollectionTypeLabel(parentData.collectionType)}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No parent information provided</div>
              )}
            </div>
          </CollapsibleSection>
        )
      }

      return (
        <div className="space-y-4">
          {shouldShowParentSections() ? (
            <>
              {shouldShowSingleParentSection() ? (
                // For F10-02, show only the parent that was selected
                <>
                  {formData.mother && formData.mother.parentType === 'mother' && (
                    <ParentalConsentSection parentData={formData.mother} parentType="mother" />
                  )}
                  {formData.father && formData.father.parentType === 'father' && (
                    <ParentalConsentSection parentData={formData.father} parentType="father" />
                  )}
                </>
              ) : (
                // For other test types, show both parents if they exist
                <>
                  {formData.mother && formData.mother.parentType === 'mother' && (
                    <ParentalConsentSection parentData={formData.mother} parentType="mother" />
                  )}
                  {formData.father && formData.father.parentType === 'father' && (
                    <ParentalConsentSection parentData={formData.father} parentType="father" />
                  )}
                </>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <p className="body-text mb-3">
                Parent consent sections are not required for rWGS Patient Only (F10-01) test type.
              </p>
            </div>
          )}
        </div>
      )
    },
  }

  const renderSectionInfo = () => {
    const renderer = sectionInfoRenderers[currentSection]
    return renderer ? renderer() : <div></div>
  }

  async function handleNextSection(
    data: OrderFormInfoData | null | undefined,
    formData: RequisitionFormData
  ): Promise<void> {
    setIsLoading(true)
    try {
      if (data && formData) {
        const order = await getOrderByPatient(data.patient.id)
        if (order.status === OrderStatus.NEW) {
          await createOrder(data, formData, OrderStatus.DRAFT)
        } else if (order.status === OrderStatus.DRAFT && order.id) {
          await updateOrder(order.id, data, formData, OrderStatus.DRAFT)
        }
      }
      const sections = [
        SECTION_NAMES.ORDER_TYPE,
        SECTION_NAMES.PATIENT_INFORMATION,
        SECTION_NAMES.CLINICAL_INFORMATION,
        SECTION_NAMES.REASON_FOR_TESTING,
        SECTION_NAMES.FAMILY_HISTORY,
        ...(shouldShowParentSections()
          ? shouldShowSingleParentSection()
            ? [SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1]
            : [SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1, SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2]
          : []),
        ...(shouldShowConsentSection() ? [SECTION_NAMES.CONSENT] : []),
        SECTION_NAMES.REVIEW_SUBMIT,
      ]
      const currentIndex = sections.indexOf(currentSection)
      if (currentIndex < sections.length - 1) {
        handleSetCurrentSection(sections[currentIndex + 1])
      }
    } catch (error) {
      console.error('Error in handleNextSection:', error)
      throw error // Re-throw to be handled by the onClick handler
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Main Content */}
      <main className="h-full w-full">
        {/* Two Column Layout */}
        <div className="flex h-full">
          {/* Left Column - Section Selection Sidebar */}
          <div className="left-column">
            <nav className="w-full">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => handleSetCurrentSection(SECTION_NAMES.ORDER_TYPE)}
                    className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                      currentSection === SECTION_NAMES.ORDER_TYPE ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Order Type</span>
                      {renderCompletionIndicator('order')}
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleSetCurrentSection(SECTION_NAMES.PATIENT_INFORMATION)}
                    className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                      currentSection === SECTION_NAMES.PATIENT_INFORMATION ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Patient Information</span>
                      {renderCompletionIndicator('patient')}
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleSetCurrentSection(SECTION_NAMES.CLINICAL_INFORMATION)}
                    className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                      currentSection === SECTION_NAMES.CLINICAL_INFORMATION ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Clinical Information</span>
                      {renderCompletionIndicator('clinical')}
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      handleSetCurrentSection(SECTION_NAMES.REASON_FOR_TESTING)
                      markSectionAsVisited('reason')
                    }}
                    className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                      currentSection === SECTION_NAMES.REASON_FOR_TESTING ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Reason for Testing</span>
                      {renderCompletionIndicator('reason')}
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleSetCurrentSection(SECTION_NAMES.FAMILY_HISTORY)}
                    className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                      currentSection === SECTION_NAMES.FAMILY_HISTORY ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Family History</span>
                      {renderCompletionIndicator('family')}
                    </div>
                  </button>
                </li>
                {shouldShowParentSections() && (
                  <>
                    {shouldShowSingleParentSection() ? (
                      <li>
                        <button
                          onClick={() => handleSetCurrentSection(SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1)}
                          className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                            currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1 ? 'bg-gray-100' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>Biological Parent Information</span>
                            {renderCompletionIndicator('mother')}
                          </div>
                        </button>
                      </li>
                    ) : (
                      <>
                        <li>
                          <button
                            onClick={() => handleSetCurrentSection(SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1)}
                            className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                              currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1 ? 'bg-gray-100' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>Biological Mother Information</span>
                              {renderCompletionIndicator('mother')}
                            </div>
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => handleSetCurrentSection(SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2)}
                            className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                              currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2 ? 'bg-gray-100' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>Biological Father Information</span>
                              {renderCompletionIndicator('father')}
                            </div>
                          </button>
                        </li>
                      </>
                    )}
                  </>
                )}
                {shouldShowConsentSection() && (
                  <li>
                    <button
                      onClick={() => handleSetCurrentSection(SECTION_NAMES.CONSENT)}
                      className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                        currentSection === SECTION_NAMES.CONSENT ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>Provider Attestation</span>
                        {renderCompletionIndicator('consent')}
                      </div>
                    </button>
                  </li>
                )}
                <li>
                  <button
                    onClick={() => handleSetCurrentSection(SECTION_NAMES.REVIEW_SUBMIT)}
                    className={`page-navigation-title relative block w-full rounded-md px-3 py-2 text-left font-sans text-gray-700 transition-colors hover:bg-gray-100 ${
                      currentSection === SECTION_NAMES.REVIEW_SUBMIT ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Review & Print</span>
                      {renderCompletionIndicator('review')}
                    </div>
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          {/* Right Column - Form Content */}
          <div className="flex h-full flex-1 flex-col bg-white">
            {/* Primary Children Component */}
            {children && <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-6">{children}</div>}

            {/* Header */}
            <div className="p-4 text-left">
              <h1 className="mb-2 text-3xl font-bold text-gray-900">Test Requisition Form</h1>
            </div>

            {/* Current Section Header and Content */}
            <div className="flex h-full min-h-0 flex-1">
              <div className="flex h-full w-full">
                {/* Left Section - Current Form Component */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="border-b border-gray-200">
                    <div className="requisition-form-section-header mb-2 flex items-center gap-3">
                      <div className="form-section-number-container">
                        <span className="form-section-number">
                          {currentSection === SECTION_NAMES.ORDER_TYPE && FormSection.ORDER_TYPE}
                          {currentSection === SECTION_NAMES.PATIENT_INFORMATION && FormSection.PATIENT_INFORMATION}
                          {currentSection === SECTION_NAMES.CLINICAL_INFORMATION && FormSection.CLINICAL_INFORMATION}
                          {currentSection === SECTION_NAMES.REASON_FOR_TESTING && FormSection.REASON_FOR_TESTING}
                          {currentSection === SECTION_NAMES.FAMILY_HISTORY && FormSection.FAMILY_HISTORY}
                          {currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1 &&
                            FormSection.BIOLOGICAL_PARENT_INFORMATION_1}
                          {currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2 &&
                            FormSection.BIOLOGICAL_PARENT_INFORMATION_2}
                          {currentSection === SECTION_NAMES.CONSENT && FormSection.CONSENT}
                          {currentSection === SECTION_NAMES.REVIEW_SUBMIT && FormSection.REVIEW_SUBMIT}
                        </span>
                      </div>
                      <h2 className="requisition-form-section-header-title page-header">
                        {currentSection === SECTION_NAMES.PATIENT_INFORMATION && 'Patient Information'}
                        {currentSection === SECTION_NAMES.CLINICAL_INFORMATION && 'Clinical Information'}
                        {currentSection === SECTION_NAMES.REASON_FOR_TESTING && 'Reason for Testing'}
                        {currentSection === SECTION_NAMES.FAMILY_HISTORY && 'Family History'}
                        {currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1 &&
                          (shouldShowSingleParentSection()
                            ? 'Biological Parent Information'
                            : 'Biological Mother Information')}
                        {currentSection === SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2 &&
                          'Biological Father Information'}
                        {currentSection === SECTION_NAMES.ORDER_TYPE && 'Order Type'}
                        {currentSection === SECTION_NAMES.CONSENT && 'Provider Attestation'}
                        {currentSection === SECTION_NAMES.REVIEW_SUBMIT && 'Review & Print'}
                      </h2>
                    </div>
                  </div>
                  {/* Scrollable Content Area */}
                  <div className="flex-1 overflow-y-auto">{renderSection()}</div>

                  {/* Fixed Navigation Buttons */}
                  <div className="border-t border-gray-200 bg-white shadow-lg">
                    <div className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            // Cancel functionality - could be enhanced to actually cancel and redirect
                            console.log('Canceling form')
                          }}
                          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Cancel
                        </button>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              const sections = [
                                SECTION_NAMES.ORDER_TYPE,
                                SECTION_NAMES.PATIENT_INFORMATION,
                                SECTION_NAMES.CLINICAL_INFORMATION,
                                SECTION_NAMES.REASON_FOR_TESTING,
                                SECTION_NAMES.FAMILY_HISTORY,
                                ...(shouldShowParentSections()
                                  ? shouldShowSingleParentSection()
                                    ? [SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1]
                                    : [
                                        SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_1,
                                        SECTION_NAMES.BIOLOGICAL_PARENT_INFORMATION_2,
                                      ]
                                  : []),
                                ...(shouldShowConsentSection() ? [SECTION_NAMES.CONSENT] : []),
                                SECTION_NAMES.REVIEW_SUBMIT,
                              ]
                              const currentIndex = sections.indexOf(currentSection)
                              if (currentIndex > 0) {
                                handleSetCurrentSection(sections[currentIndex - 1])
                              }
                            }}
                            disabled={currentSection === SECTION_NAMES.ORDER_TYPE}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Back
                          </button>

                          {currentSection === SECTION_NAMES.REVIEW_SUBMIT ? (
                            <>
                              <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!isFormComplete()}
                                className="rounded-md border border-transparent bg-green-600 px-6 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Print Order
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await handleNextSection(data, formData)
                                } catch (error) {
                                  console.error('Error handling next section:', error)
                                  // You might want to show a user-friendly error message here
                                  alert('An error occurred while saving your progress. Please try again.')
                                }
                              }}
                              disabled={isLoading}
                              className="rounded-md border border-transparent bg-indigo-600 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isLoading ? 'Saving...' : 'Next'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section - Dynamic Information */}
                <div className="requisition-form-section-info flex h-full w-80 flex-col">
                  <div className="flex-1 overflow-y-auto">{renderSectionInfo()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default RequisitionForm
